import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

/**
 * "Data through Sep 13" — the answer to the question a customer asks before
 * they trust any figure on the screen.
 *
 * It reads `api_config.last_update_date` from the tenant's own database, the
 * same column the sandbox stamps answers from, so this pill and an answer's
 * freshness line can never disagree with each other.
 *
 * It renders nothing at all when the date is unknown. A pill that said
 * "unknown" would be worse than no pill: the September 2026 outage ran for five
 * nights precisely because a job reported success while loading nothing, and a
 * reassuring-looking widget with no data behind it is the same failure again.
 *
 * The pill is also the way into Know your data, whose first section is what
 * Signals holds of each topic and up to when. Admins have no company data, so
 * the pill never shows for them.
 */

interface Freshness {
  updated_through: string | null;
  oldest_topic?: string | null;
  topics: number;
  /** Days between the freshest and the stalest enabled topic. */
  stale: number | null;
}

const formatDay = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** The ETL lands once a night, so re-asking every 10 minutes is ample and cheap
 *  (one indexed MAX() on api_config). */
const REFRESH_MS = 10 * 60 * 1000;

export default function FreshnessPill() {
  const [data, setData] = useState<Freshness | null>(null);
  const navigate = useNavigate();

  // Re-check periodically, and whenever the tab is brought back to the front.
  // Fetching once on mount was the bug this pill was written to prevent: during
  // the five nights the ETL loaded nothing, anyone who left the tab open kept
  // reading a date that had stopped being true, and the amber dot never lit
  // because nothing asked again.
  useEffect(() => {
    let cancelled = false;

    const check = () => {
      fetch('/api/data/freshness', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!cancelled) setData(d);
        })
        .catch(() => {
          /* keep whatever we last knew rather than blanking the pill */
        });
    };

    check();
    const timer = window.setInterval(check, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!data?.updated_through) return null;

  // Amber when some enabled topic lags the rest by more than a day. That is the
  // shape the frozen-ETL incident had: most topics current, a few weeks behind.
  const lagging = typeof data.stale === 'number' && data.stale > 1;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => navigate('/know-your-data')}
          aria-label="Know your data"
          dir="ltr"
          // Visible on every screen, phones included. It was hidden below
          // `sm`, which removed it from exactly the device most likely to be
          // read in the morning without reloading -- and "how old is this
          // data" is not a desktop-only question. The date collapses to the
          // day number on narrow screens so it still fits beside the nav.
          className="flex items-center gap-1.5 text-[11.5px] rounded-full px-[7px] sm:px-[9px] py-1 whitespace-nowrap cursor-pointer hover:opacity-80"
          style={{
            background: 'var(--sig-panel-deep)',
            border: '1px solid var(--sig-border)',
            color: 'var(--sig-muted)',
            fontFamily: 'Inter, sans-serif'
          }}
        >
          <span
            className="block rounded-full"
            style={{
              width: 6,
              height: 6,
              flex: 'none',
              background: lagging
                ? 'var(--sig-warn-icon)'
                : 'var(--sig-positive)'
            }}
          />
          {/* On a phone the two words are dropped, not the date: the point of
              the pill is the date, and the dot already says whether it is ok. */}
          <span className="hidden sm:inline">Data through&nbsp;</span>
          {formatDay(data.updated_through)}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {lagging
          ? `${data.topics} topics. The oldest was last synced ${data.stale} days before the newest — some data is behind.`
          : `${data.topics} topics, all synced together.`}
        {' Click to see what Signals holds of your data.'}
      </TooltipContent>
    </Tooltip>
  );
}
